
import React, { useState } from 'react';
import { IPVEntry } from '../types';
import { IconIPV, IconSearch } from '../components/Icons';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { analyzeRiskScenario } from '../services/geminiService';

// Mock IPV Data
const mockIpvData: IPVEntry[] = [
  { id: 'IPV-001', instrument: 'US T-Note 10Y', positionId: 'POS-4821', internalPrice: 98.50, consensusPrice: 98.48, variance: 0.02, dollarImpact: 15000, confidence: 'High', source: 'Totem', status: 'Pass' },
  { id: 'IPV-002', instrument: 'SPX Option Dec24 4500C', positionId: 'POS-3321', internalPrice: 124.50, consensusPrice: 121.00, variance: 2.89, dollarImpact: -350000, confidence: 'High', source: 'Bloomberg BVAL', status: 'Investigation' },
  { id: 'IPV-003', instrument: 'EUR/USD Forward 6M', positionId: 'POS-9982', internalPrice: 1.1240, consensusPrice: 1.1242, variance: -0.01, dollarImpact: -500, confidence: 'Medium', source: 'Broker', status: 'Pass' },
  { id: 'IPV-004', instrument: 'Corp Bond: Evergrande', positionId: 'POS-1120', internalPrice: 15.00, consensusPrice: 12.50, variance: 20.00, dollarImpact: -1250000, confidence: 'Low', source: 'Markit', status: 'Investigation' },
  { id: 'IPV-005', instrument: 'Barrier Opt USDJPY', positionId: 'POS-7763', internalPrice: 2.45, consensusPrice: 2.44, variance: 0.41, dollarImpact: 2400, confidence: 'Medium', source: 'Totem', status: 'Pass' },
];

const IPVControlView: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<IPVEntry | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Prepare data for Scatter Chart (Internal vs Consensus)
  const chartData = mockIpvData.map(item => ({
    x: item.internalPrice,
    y: item.consensusPrice,
    name: item.instrument,
    status: item.status
  }));

  const handleAnalyzeVariance = async (item: IPVEntry) => {
    setIsAnalyzing(true);
    setAiAnalysis('');
    
    const prompt = `
      You are a Product Controller. There is a valuation variance for instrument "${item.instrument}".
      
      Internal Price: ${item.internalPrice}
      Consensus Price (Source: ${item.source}): ${item.consensusPrice}
      Variance: ${item.variance}%
      Dollar Impact: $${item.dollarImpact}
      Confidence of Source: ${item.confidence}
      
      Provide a short investigation plan. What could cause this variance? (e.g., Model params, liquidity, timing mismatch). 
      Should we adjust our mark?
    `;

    const response = await analyzeRiskScenario(prompt, { item });
    setAiAnalysis(response);
    setIsAnalyzing(false);
  };

  return (
    <div className="p-6 h-full overflow-y-auto custom-scrollbar space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Independent Price Verification</h2>
          <p className="text-slate-500">Month-end Valuations, Consensus Matching & Variance Analysis</p>
        </div>
        <div className="flex gap-2">
           <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow-sm text-sm font-medium">
             Upload Totem File
           </button>
           <button className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded shadow-sm text-sm font-medium">
             Generate Report
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Charting */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-800 mb-2">Valuation Alignment (Internal vs. Consensus)</h3>
          <p className="text-xs text-slate-400 mb-4">Points on the diagonal line represent perfect alignment. Outliers require investigation.</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" dataKey="x" name="Internal" unit="$" stroke="#94a3b8" fontSize={12} label={{ value: 'Internal Price', position: 'insideBottom', offset: -10, fontSize: 10 }} />
                <YAxis type="number" dataKey="y" name="Consensus" unit="$" stroke="#94a3b8" fontSize={12} label={{ value: 'Consensus Price', angle: -90, position: 'insideLeft', fontSize: 10 }}/>
                <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ payload }) => {
                    if (payload && payload.length) {
                        return <div className="bg-white p-2 border border-slate-200 text-xs shadow-md rounded">
                            <p className="font-bold">{payload[0].payload.name}</p>
                            <p>Int: {payload[0].payload.x}</p>
                            <p>Con: {payload[0].payload.y}</p>
                        </div>
                    }
                    return null;
                }}/>
                {/* Perfect Alignment Line */}
                <ReferenceLine segment={[{x: 0, y: 0}, {x: 200, y: 200}]} stroke="#cbd5e1" strokeDasharray="3 3" />
                <Scatter name="Positions" data={chartData} fill="#3b82f6" />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="space-y-4">
           <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
             <h3 className="font-semibold text-slate-800 mb-4">Validation Summary</h3>
             <div className="space-y-4">
                <div className="flex justify-between items-center">
                   <span className="text-sm text-slate-500">Total Positions</span>
                   <span className="font-mono font-medium">1,240</span>
                </div>
                <div className="flex justify-between items-center">
                   <span className="text-sm text-slate-500">Matched (&gt;99% conf)</span>
                   <span className="font-mono font-medium text-emerald-600">1,180 (95%)</span>
                </div>
                <div className="flex justify-between items-center">
                   <span className="text-sm text-slate-500">Flagged for Review</span>
                   <span className="font-mono font-medium text-amber-600">45 (3.6%)</span>
                </div>
                <div className="flex justify-between items-center">
                   <span className="text-sm text-slate-500">Critical Breaks</span>
                   <span className="font-mono font-medium text-red-600">15 (1.2%)</span>
                </div>
             </div>
             <div className="mt-6 pt-6 border-t border-slate-100">
                <div className="text-xs text-slate-400 uppercase mb-1">Total Variance Impact</div>
                <div className="text-2xl font-bold text-red-600">-$3.25M</div>
             </div>
           </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden flex flex-col lg:flex-row">
         <div className="flex-1 overflow-x-auto">
            <table className="w-full text-sm text-left">
               <thead className="bg-slate-50 text-slate-500">
                  <tr>
                     <th className="px-6 py-3 font-medium">ID</th>
                     <th className="px-6 py-3 font-medium">Instrument</th>
                     <th className="px-6 py-3 font-medium text-right">Internal Px</th>
                     <th className="px-6 py-3 font-medium text-right">Consensus Px</th>
                     <th className="px-6 py-3 font-medium text-right">Var %</th>
                     <th className="px-6 py-3 font-medium">Status</th>
                     <th className="px-6 py-3 font-medium">Action</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-100">
                  {mockIpvData.map((item) => (
                     <tr 
                       key={item.id} 
                       className={`hover:bg-slate-50 transition-colors ${selectedItem?.id === item.id ? 'bg-blue-50' : ''}`}
                       onClick={() => setSelectedItem(item)}
                     >
                        <td className="px-6 py-3 text-slate-600 font-mono text-xs">{item.id}</td>
                        <td className="px-6 py-3 font-medium text-slate-800">{item.instrument}</td>
                        <td className="px-6 py-3 text-right text-slate-600">{item.internalPrice.toFixed(2)}</td>
                        <td className="px-6 py-3 text-right text-slate-600">{item.consensusPrice.toFixed(2)}</td>
                        <td className={`px-6 py-3 text-right font-bold ${Math.abs(item.variance) > 1 ? 'text-red-600' : 'text-emerald-600'}`}>
                           {item.variance > 0 ? '+' : ''}{item.variance.toFixed(2)}%
                        </td>
                        <td className="px-6 py-3">
                           {item.status === 'Pass' && <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded font-bold">PASS</span>}
                           {item.status === 'Investigation' && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded font-bold">INVESTIGATE</span>}
                        </td>
                        <td className="px-6 py-3">
                            <button 
                              className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                              onClick={(e) => { e.stopPropagation(); setSelectedItem(item); }}
                            >
                                Detail
                            </button>
                        </td>
                     </tr>
                  ))}
               </tbody>
            </table>
         </div>

         {/* Detail Side Panel */}
         {selectedItem && (
             <div className="w-full lg:w-96 border-l border-slate-200 bg-slate-50 p-6 flex flex-col overflow-y-auto custom-scrollbar">
                 <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-slate-800 text-lg">Variance Detail</h3>
                    <button onClick={() => setSelectedItem(null)} className="text-slate-400 hover:text-slate-600">×</button>
                 </div>

                 <div className="space-y-4 mb-6">
                    <div className="bg-white p-4 rounded border border-slate-200 shadow-sm">
                        <div className="text-xs text-slate-400">Dollar Impact</div>
                        <div className={`text-xl font-bold ${selectedItem.dollarImpact < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {selectedItem.dollarImpact < 0 ? '-' : '+'}${Math.abs(selectedItem.dollarImpact).toLocaleString()}
                        </div>
                    </div>
                    
                    <div>
                        <p className="text-xs text-slate-500 uppercase font-bold mb-1">Source Quality</p>
                        <p className="text-sm text-slate-800 flex items-center gap-2">
                            {selectedItem.source}
                            <span className={`px-1.5 py-0.5 rounded text-[10px] border ${
                                selectedItem.confidence === 'High' ? 'bg-green-100 border-green-200 text-green-700' : 'bg-amber-100 border-amber-200 text-amber-700'
                            }`}>
                                {selectedItem.confidence} Confidence
                            </span>
                        </p>
                    </div>

                    <button 
                        onClick={() => handleAnalyzeVariance(selectedItem)}
                        disabled={isAnalyzing}
                        className="w-full py-2 bg-slate-800 text-white rounded text-sm font-medium hover:bg-slate-900 flex justify-center items-center gap-2"
                    >
                        {isAnalyzing ? (
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        ) : (
                            <IconIPV />
                        )}
                        Analyze with AiQ
                    </button>
                 </div>

                 {/* AI Result */}
                 {aiAnalysis && (
                     <div className="bg-blue-50 border border-blue-100 p-4 rounded text-sm text-blue-900 leading-relaxed">
                         <h5 className="font-bold text-blue-700 mb-2 text-xs uppercase">AiQ Investigation Plan</h5>
                         {aiAnalysis}
                     </div>
                 )}
             </div>
         )}
      </div>
    </div>
  );
};

export default IPVControlView;
